<template>
    <q-page padding class="roles-page">
        <!-- Header moderno mejorado -->
        <div class="page-header">
            <div class="row items-center no-wrap">
                <q-avatar size="56px" color="primary" text-color="white">
                    <q-icon name="admin_panel_settings" size="32px" />
                </q-avatar>
                <div class="col q-ml-md">
                    <div class="text-h4 text-weight-bold text-grey-9">Gestión de Roles y Permisos</div>
                    <div class="text-subtitle2 text-grey-6">
                        Administre roles, permisos y asignaciones de usuarios del sistema
                    </div>
                </div>
                <q-chip
                    v-if="roleActive"
                    color="primary"
                    text-color="white"
                    icon="check_circle"
                    class="chip-active"
                >
                    {{ roles.find(r => r.id === roleActive)?.name }}
                </q-chip>
            </div>
        </div>

        <div class="row q-col-gutter-lg q-mt-md">
            <!-- Columna 1: Listado de Roles -->
            <div class="col-12 col-md-4">
                <q-card class="roles-card">
                    <q-card-section class="card-header">
                        <div class="row items-center no-wrap">
                            <q-icon name="mdi-shield-home" size="28px" class="q-mr-sm" />
                            <div class="col">
                                <div class="text-h6 text-weight-bold">Roles Disponibles</div>
                                <div class="text-caption text-white-7 q-mt-xs">
                                    {{ roles.length }} {{ roles.length === 1 ? 'rol disponible' : 'roles disponibles' }}
                                </div>
                            </div>
                        </div>
                    </q-card-section>

                    <q-separator />

                    <q-card-section class="q-pa-none">
                        <q-scroll-area style="height: 500px">
                            <q-list>
                                <template v-for="(role, index) in roles" :key="role.id">
                                    <q-item
                                        @click="onRoleClick(role)"
                                        clickable
                                        v-ripple
                                        :active="roleActive === role.id"
                                        active-class="role-item-active"
                                        class="role-item"
                                    >
                                        <q-item-section avatar>
                                            <q-avatar
                                                :color="roleActive === role.id ? 'primary' : 'purple-1'"
                                                :text-color="roleActive === role.id ? 'white' : 'purple-9'"
                                                size="48px"
                                            >
                                                <q-icon
                                                    :name="roleActive === role.id ? 'verified' : 'shield'"
                                                    size="28px"
                                                />
                                            </q-avatar>
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label class="text-weight-bold text-grey-9">
                                                {{ role.name }}
                                            </q-item-label>
                                            <q-item-label caption class="text-grey-6">
                                                {{ role.description || 'Sin descripción disponible' }}
                                            </q-item-label>
                                        </q-item-section>
                                        <q-item-section side v-if="roleActive === role.id">
                                            <q-icon name="arrow_forward_ios" color="primary" size="18px" />
                                        </q-item-section>
                                    </q-item>
                                    <q-separator v-if="index < roles.length - 1" />
                                </template>
                            </q-list>
                        </q-scroll-area>
                    </q-card-section>

                    <q-separator />

                    <q-card-section class="q-pa-sm bg-grey-1">
                        <div class="text-caption text-center text-grey-7">
                            <q-icon name="info" size="16px" class="q-mr-xs" />
                            Seleccione un rol para gestionar permisos y usuarios
                        </div>
                    </q-card-section>
                </q-card>
            </div>

            <!-- Columna 2: Permisos -->
            <div class="col-12 col-md-4">
                <q-card class="permissions-card">
                    <q-card-section class="card-header-permissions">
                        <div class="row items-center no-wrap">
                            <q-icon name="verified_user" size="28px" class="q-mr-sm" />
                            <div class="col">
                                <div class="text-h6 text-weight-bold">Permisos del Rol</div>
                                <div class="text-caption text-white-7 q-mt-xs">
                                    {{ permissions.length }} {{ permissions.length === 1 ? 'permiso asignado' : 'permisos asignados' }}
                                </div>
                            </div>
                        </div>
                    </q-card-section>

                    <q-separator />

                    <!-- Sección para agregar permiso -->
                    <q-card-section v-if="roleActive" class="q-pa-md">
                        <q-expansion-item
                            icon="add_circle"
                            label="Asignar Nuevo Permiso"
                            header-class="bg-green-1 text-green-9 text-weight-medium rounded-borders"
                            class="expansion-add"
                        >
                            <q-card flat bordered class="q-mt-sm">
                                <q-card-section>
                                    <div class="row q-col-gutter-sm">
                                        <div class="col-12">
                                            <GltnSelectPermisos
                                                v-model="permission"
                                                label="Seleccionar Permiso"
                                                :multiple="false"
                                                :required="true"
                                            >
                                                <template v-slot:prepend>
                                                    <q-icon name="lock_open" color="green" />
                                                </template>
                                            </GltnSelectPermisos>
                                        </div>
                                        <div class="col-12">
                                            <q-btn
                                                unelevated
                                                :loading="permissionAddLoading"
                                                color="positive"
                                                icon="add"
                                                label="Asignar Permiso al Rol"
                                                @click="addPermissionToRole(permission)"
                                                :disable="!permission"
                                                class="full-width action-btn"
                                                no-caps
                                            />
                                        </div>
                                    </div>
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-card-section>

                    <q-separator v-if="roleActive" />

                    <q-card-section class="q-pa-none" v-if="roleActive">
                        <q-scroll-area style="height: 380px">
                            <q-list v-if="permissions.length > 0" separator>
                                <template
                                    v-for="permission in permissions"
                                    :key="permission.id"
                                >
                                    <q-item class="permission-item">
                                        <q-item-section avatar>
                                            <q-avatar color="green-1" text-color="green-9" size="40px">
                                                <q-icon name="check_circle" size="22px" />
                                            </q-avatar>
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label class="text-weight-bold text-grey-9">
                                                {{ permission.display }}
                                            </q-item-label>
                                            <q-item-label caption class="text-grey-6">
                                                {{ permission.obs || 'Sin observaciones adicionales' }}
                                            </q-item-label>
                                        </q-item-section>
                                        <q-item-section side>
                                            <q-btn
                                                unelevated
                                                dense
                                                color="red"
                                                icon="delete"
                                                @click="removePermissionFromRole(permission.id)"
                                                :loading="permissionRoleRemoveLoading === permission.id"
                                                round
                                                size="sm"
                                            >
                                                <q-tooltip>Eliminar permiso del rol</q-tooltip>
                                            </q-btn>
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </q-list>
                            <div v-else class="empty-state">
                                <q-icon name="info_outline" size="64px" color="grey-4" />
                                <div class="text-h6 text-grey-6 q-mt-md">Sin permisos</div>
                                <div class="text-caption text-grey-5 q-mt-sm">
                                    Este rol no tiene permisos asignados
                                </div>
                            </div>
                        </q-scroll-area>
                    </q-card-section>

                    <q-card-section v-else class="empty-state">
                        <q-icon name="touch_app" size="72px" color="grey-4" />
                        <div class="text-h6 text-grey-6 q-mt-md">Seleccione un rol</div>
                        <div class="text-caption text-grey-5 q-mt-sm">
                            Haga clic en un rol de la lista para gestionar sus permisos
                        </div>
                    </q-card-section>

                    <q-inner-loading :showing="loading">
                        <q-spinner-hourglass size="50px" color="primary" />
                    </q-inner-loading>
                </q-card>
            </div>

            <!-- Columna 3: Usuarios -->
            <div class="col-12 col-md-4">
                <q-card class="users-card">
                    <q-card-section class="card-header-users">
                        <div class="row items-center no-wrap">
                            <q-icon name="mdi-shield-account" size="28px" class="q-mr-sm" />
                            <div class="col">
                                <div class="text-h6 text-weight-bold">Usuarios del Rol</div>
                                <div class="text-caption text-white-7 q-mt-xs">
                                    {{ users.length }} {{ users.length === 1 ? 'usuario asignado' : 'usuarios asignados' }}
                                </div>
                            </div>
                        </div>
                    </q-card-section>

                    <q-separator />

                    <!-- Sección para asignar usuario -->
                    <q-card-section v-if="roleActive" class="q-pa-md">
                        <q-expansion-item
                            icon="person_add"
                            label="Asignar Nuevo Usuario"
                            header-class="bg-blue-1 text-blue-9 text-weight-medium rounded-borders"
                            class="expansion-add"
                        >
                            <q-card flat bordered class="q-mt-sm">
                                <q-card-section>
                                    <div class="row q-col-gutter-sm">
                                        <div class="col-12">
                                            <GltnSelectUsuarios
                                                v-model="user"
                                                label="Seleccionar Usuario"
                                                :multiple="false"
                                                :required="true"
                                            >
                                                <template v-slot:prepend>
                                                    <q-icon name="person_search" color="blue" />
                                                </template>
                                            </GltnSelectUsuarios>
                                        </div>
                                        <div class="col-12">
                                            <q-btn
                                                unelevated
                                                :loading="roleAssignLoading"
                                                color="primary"
                                                icon="add"
                                                label="Asignar Usuario al Rol"
                                                @click="assignRoleToUser(user)"
                                                :disable="!user"
                                                class="full-width action-btn"
                                                no-caps
                                            />
                                        </div>
                                    </div>
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-card-section>

                    <q-separator v-if="roleActive" />

                    <q-card-section class="q-pa-none" v-if="roleActive">
                        <q-scroll-area style="height: 380px">
                            <q-list v-if="users.length > 0" separator>
                                <template v-for="user in users" :key="user.id">
                                    <q-item class="user-item">
                                        <q-item-section avatar>
                                            <q-avatar
                                                color="blue-1"
                                                text-color="blue-9"
                                                size="44px"
                                            >
                                                <q-img
                                                    :src="`https://api.dicebear.com/7.x/initials/svg?seed=${user.name}`"
                                                    :alt="user.name"
                                                />
                                            </q-avatar>
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label class="text-weight-bold text-grey-9">
                                                {{ user.name }}
                                            </q-item-label>
                                            <q-item-label caption class="text-grey-6">
                                                <q-icon name="email" size="14px" class="q-mr-xs" />
                                                {{ user.email }}
                                            </q-item-label>
                                        </q-item-section>
                                        <q-item-section side>
                                            <q-btn
                                                unelevated
                                                dense
                                                color="red"
                                                icon="delete"
                                                @click="removeUserFromRole(user.id)"
                                                :loading="roleRemoveLoading === user.id"
                                                round
                                                size="sm"
                                            >
                                                <q-tooltip>Eliminar usuario del rol</q-tooltip>
                                            </q-btn>
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </q-list>
                            <div v-else class="empty-state">
                                <q-icon name="person_off" size="64px" color="grey-4" />
                                <div class="text-h6 text-grey-6 q-mt-md">Sin usuarios</div>
                                <div class="text-caption text-grey-5 q-mt-sm">
                                    Este rol no tiene usuarios asignados
                                </div>
                            </div>
                        </q-scroll-area>
                    </q-card-section>

                    <q-card-section v-else class="empty-state">
                        <q-icon name="touch_app" size="72px" color="grey-4" />
                        <div class="text-h6 text-grey-6 q-mt-md">Seleccione un rol</div>
                        <div class="text-caption text-grey-5 q-mt-sm">
                            Haga clic en un rol de la lista para gestionar sus usuarios
                        </div>
                    </q-card-section>

                    <q-inner-loading :showing="loading">
                        <q-spinner-hourglass size="50px" color="primary" />
                    </q-inner-loading>
                </q-card>
            </div>
        </div>
    </q-page>
</template>

<script setup>
import { ref } from "vue";
import RoleService from "@/services/RoleService.js";
import UserService from "@/services/UserService.js";
import GltnSelectUsuarios from "@/components/form/GltnSelectUsuarios.vue";
import GltnSelectPermisos from "@/components/form/GltnSelectPermisos.vue";
import { useQuasar } from "quasar";
const $q = useQuasar();

const { roles } = defineProps({ roles: Array });
const permissions = ref([]);
const users = ref([]);
const loading = ref(false);
const roleActive = ref(null);
const user = ref(null);
const permission = ref(null);
const roleAssignLoading = ref(false);
const permissionAddLoading = ref(false);
const roleRemoveLoading = ref(null);
const permissionRoleRemoveLoading = ref(null);

const onRoleClick = async (role) => {
    roleActive.value = role.id;
    loading.value = true;
    const roleData = await RoleService.show(role.id);
    permissions.value = roleData.permissions;
    users.value = roleData.users;
    loading.value = false;
};

const assignRoleToUser = async (userId) => {
    roleAssignLoading.value = true;
    if (!userId || !roleActive.value) {
        $q.notify({
            color: "red",
            message: "Debe seleccionar un usuario y un rol",
            icon: "error",
        });
        roleAssignLoading.value = false;
        return;
    }
    try {
        await UserService.assignRole(userId, roleActive.value);
        $q.notify({
            color: "green",
            message: "Rol asignado con éxito",
            icon: "check",
        });
        onRoleClick({ id: roleActive.value });
    } catch (error) {
        console.error("Error assigning role to user:", error);
        $q.notify({
            color: "red",
            message: "Error al asignar rol al usuario",
            icon: "error",
        });
    } finally {
        roleAssignLoading.value = false;
    }
};

const removeUserFromRole = async (userId) => {
    roleRemoveLoading.value = userId;
    if (!userId || !roleActive.value) {
        $q.notify({
            color: "red",
            message: "Debe seleccionar un usuario y un rol",
            icon: "error",
        });
        roleRemoveLoading.value = null;
        return;
    }
    try {
        await UserService.removeRole(userId, roleActive.value);
        $q.notify({
            color: "green",
            message: "Rol eliminado con éxito",
            icon: "check",
        });
        onRoleClick({ id: roleActive.value });
    } catch (error) {
        console.error("Error removing role from user:", error);
        $q.notify({
            color: "red",
            message: "Error al eliminar rol del usuario",
            icon: "error",
        });
    } finally {
        roleRemoveLoading.value = null;
    }
};

const addPermissionToRole = async (permissionId) => {
    if (!permissionId || !roleActive.value) {
        $q.notify({
            color: "red",
            message: "Debe seleccionar un permiso y un rol",
            icon: "error",
        });
        return;
    }
    permissionAddLoading.value = true;
    try {
        await RoleService.addRolePermission(roleActive.value, permissionId);
        onRoleClick({ id: roleActive.value });
    } catch (error) {
        console.error("Error adding permission to role:", error);
        $q.notify({
            color: "red",
            message: "Error al agregar permiso al rol",
            icon: "error",
        });
    } finally {
        permissionAddLoading.value = false;
    }
};
const removePermissionFromRole = async (permissionId) => {
    if (!permissionId || !roleActive.value) {
        $q.notify({
            color: "red",
            message: "Debe seleccionar un permiso y un rol",
            icon: "error",
        });
        return;
    }
    permissionRoleRemoveLoading.value = permissionId;
    try {
        await RoleService.removeRolePermission(roleActive.value, permissionId);
        onRoleClick({ id: roleActive.value });
    } catch (error) {
        console.error("Error removing permission from role:", error);
        $q.notify({
            color: "red",
            message: "Error al eliminar permiso del rol",
            icon: "error",
        });
    } finally {
        permissionRoleRemoveLoading.value = null;
    }
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.roles-page {
    background: linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%);
    min-height: 100vh;
}

.page-header {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    margin-bottom: 24px;
    animation: fadeInDown 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
}

.chip-active {
    font-size: 14px;
    padding: 8px 16px;
    font-weight: 600;
}

.roles-card,
.permissions-card,
.users-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
    }
}

.card-header {
    background: linear-gradient(135deg, #5e35b1 0%, #4527a0 100%);
    color: white;
    padding: 24px;
}

.card-header-permissions {
    background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
    color: white;
    padding: 24px;
}

.card-header-users {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    padding: 24px;
}

.text-white-7 {
    color: rgba(255, 255, 255, 0.85);
}

// Items de rol
.role-item {
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
    padding: 16px;

    &:hover {
        background: linear-gradient(to right, #f3e5f5 0%, #fafafa 100%);
        transform: translateX(8px);
        border-left-color: #5e35b1;
    }

    &.role-item-active {
        background: linear-gradient(to right, #e8eaf6 0%, #e3f2fd 100%);
        border-left-color: var(--q-primary);
        box-shadow: inset 0 0 0 1px rgba(94, 53, 177, 0.1);
    }
}

// Items de permiso
.permission-item {
    transition: all 0.3s ease;
    padding: 14px;

    &:hover {
        background: linear-gradient(to right, #e8f5e9 0%, #fafafa 100%);
        transform: translateX(6px);
    }
}

// Items de usuario
.user-item {
    transition: all 0.3s ease;
    padding: 14px;

    &:hover {
        background: linear-gradient(to right, #e3f2fd 0%, #fafafa 100%);
        transform: translateX(6px);
    }
}

// Estados vacíos
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    text-align: center;
}

// Expansion items mejorados
.expansion-add {
    :deep(.q-expansion-item__container) {
        border: 2px solid rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
            border-color: rgba(0, 0, 0, 0.15);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
    }

    :deep(.q-item) {
        min-height: 48px;
        border-radius: 12px;
    }
}

// Botón de acción
.action-btn {
    font-weight: 600;
    padding: 10px 20px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
}

// Scroll areas personalizadas
:deep(.q-scrollarea__content) {
    &::-webkit-scrollbar {
        width: 10px;
    }

    &::-webkit-scrollbar-track {
        background: linear-gradient(to bottom, #f5f5f5 0%, #eeeeee 100%);
        border-radius: 5px;
    }

    &::-webkit-scrollbar-thumb {
        background: linear-gradient(to bottom, #bdbdbd 0%, #9e9e9e 100%);
        border-radius: 5px;
        transition: background 0.3s ease;

        &:hover {
            background: linear-gradient(to bottom, #9e9e9e 0%, #757575 100%);
        }
    }
}

// Animaciones
@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.roles-card {
    animation: fadeIn 0.4s ease 0.1s backwards;
}

.permissions-card {
    animation: fadeIn 0.4s ease 0.2s backwards;
}

.users-card {
    animation: fadeIn 0.4s ease 0.3s backwards;
}

// Responsive
@media (max-width: 1023px) {
    .page-header {
        border-radius: 12px;
        padding: 20px;
    }

    .roles-card,
    .permissions-card,
    .users-card {
        border-radius: 12px;
        margin-bottom: 16px;
    }

    .empty-state {
        padding: 48px 24px;
    }
}

// Mejoras adicionales de botones
:deep(.q-btn) {
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
    }

    &.q-btn--round {
        &:hover:not(:disabled) {
            transform: scale(1.1);
        }
    }
}

// Badges y chips
:deep(.q-badge),
:deep(.q-chip) {
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.05);
    }
}
</style>
